import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, MapPin, Phone, Globe, Clock, MessageSquare, Image as ImageIcon,
  TrendingUp, Eye, Search, ArrowLeft, Plus, Sparkles, Link as LinkIcon, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

const mockReviews = [
  { id: '1', author: 'Olivia G.', rating: 5, text: 'Best little florist in Leeds. Sarah remembered my anniversary even before I did!', date: '2 days ago', responded: false },
  { id: '2', author: 'James W.', rating: 4, text: 'Lovely arrangements but a bit pricey. Worth it for special occasions.', date: '5 days ago', responded: true },
  { id: '3', author: 'Priya K.', rating: 2, text: 'Order arrived an hour late and a few flowers looked tired. Hoping for better next time.', date: '1 week ago', responded: false },
  { id: '4', author: 'Tom A.', rating: 5, text: 'Beautiful bouquet for my mum’s 70th. Made her cry happy tears!', date: '2 weeks ago', responded: true },
];

const mockMetrics = [
  { label: 'Profile views (30d)', value: '2,184', change: '+18%', icon: Eye },
  { label: 'Search appearances', value: '8,470', change: '+12%', icon: Search },
  { label: 'Direction requests', value: '147', change: '+22%', icon: MapPin },
  { label: 'Phone calls', value: '63', change: '+9%', icon: Phone },
];

const GBP = () => {
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [reviews, setReviews] = useState(mockReviews);
  const [activeReview, setActiveReview] = useState(null);
  const [reply, setReply] = useState('');
  const [generating, setGenerating] = useState(false);

  const connect = () => {
    setTimeout(() => {
      setConnected(true);
      toast({ title: 'Google Business Profile linked (demo)', description: 'Live OAuth ready when developer keys provided.' });
    }, 600);
  };

  const generateReply = async (rev) => {
    setActiveReview(rev);
    setReply('');
    setGenerating(true);
    try {
      const { data } = await api.post('/reviews/respond', {
        review: rev.text,
        rating: rev.rating,
        businessName: 'Bloom & Petal Florists',
        businessType: 'retail',
        tone: rev.rating <= 3 ? 'apologetic' : 'friendly',
      });
      setReply(data.reply);
    } catch (err) {
      toast({ title: 'Could not generate', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const postReply = () => {
    if (!activeReview) return;
    setReviews(prev => prev.map(r => r.id === activeReview.id ? { ...r, responded: true } : r));
    setActiveReview(null);
    setReply('');
    toast({ title: 'Reply posted (demo)', description: 'Will sync to Google when GBP API is connected.' });
  };

  if (!connected) {
    return (
      <div className="min-h-screen bg-[#fdfaf6]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center">
              <GoogleBadge size={32} />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold text-slate-900 tracking-tight">Connect Google Business Profile</h1>
            <p className="mt-2 text-slate-600 max-w-md mx-auto">
              Manage your listing, photos, and reviews — and reply to every review with one click using AI.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-3 text-left">
              {[
                { icon: MessageSquare, t: 'AI review replies', d: 'On-brand responses to every review' },
                { icon: ImageIcon, t: 'Manage photos', d: 'Upload products, storefront' },
                { icon: TrendingUp, t: 'Local SEO insights', d: 'Track views, calls, directions' },
              ].map((f, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-4">
                  <f.icon className="w-5 h-5 text-orange-500" />
                  <div className="mt-2 font-semibold text-sm">{f.t}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{f.d}</div>
                </div>
              ))}
            </div>
            <Button onClick={connect} className="mt-8 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold px-7">
              <GoogleBadge size={18} /> <span className="ml-2">Connect Google Business Profile</span>
            </Button>
            <p className="mt-3 text-xs text-slate-400">Demo connection — real OAuth when developer keys are provided.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header card with profile */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-wrap items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold text-slate-900">Bloom &amp; Petal Florists</h1>
              <Badge className="bg-emerald-100 text-emerald-800 border-0"><Check className="w-3 h-3 mr-1" /> Connected</Badge>
            </div>
            <div className="text-sm text-slate-500 flex items-center gap-3 flex-wrap mt-1">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> 24 High Street, Leeds LS1 4DT</span>
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> 0113 234 5678</span>
              <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> bloomflorists.co.uk</span>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-xl">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
            <span className="text-2xl font-extrabold text-slate-900">4.7</span>
            <span className="text-sm text-slate-500">· 184 reviews</span>
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {mockMetrics.map(m => (
            <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <m.icon className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-xs text-emerald-600 font-semibold">{m.change}</span>
              </div>
              <div className="mt-3 text-2xl font-extrabold text-slate-900">{m.value}</div>
              <div className="text-xs text-slate-500">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="reviews" className="mt-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="info">Business Info</TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="mt-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {reviews.map(r => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{r.author}</div>
                      <div className="flex items-center gap-1 mt-1">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`w-4 h-4 ${i <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                        ))}
                        <span className="text-xs text-slate-400 ml-1">{r.date}</span>
                      </div>
                    </div>
                    {r.responded && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Replied</Badge>}
                  </div>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed">{r.text}</p>
                  {!r.responded && (
                    <Button onClick={() => generateReply(r)} size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800 text-white">
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI reply
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center">
              <ImageIcon className="w-10 h-10 mx-auto text-slate-300" />
              <h3 className="mt-3 font-bold text-slate-900">Post to Google soon</h3>
              <p className="text-sm text-slate-500">Share offers and updates directly to your GBP listing.</p>
              <Button className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => navigate('/create')}>
                <Plus className="w-4 h-4 mr-1.5" /> Create a post
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="info" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-sm text-slate-700 space-y-2">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Mon–Sat 9:00–18:00 · Sun closed</div>
              <div className="flex items-center gap-2"><LinkIcon className="w-4 h-4 text-slate-400" /> bloomflorists.co.uk</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> 0113 234 5678</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> 24 High Street, Leeds LS1 4DT</div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reply modal */}
      {activeReview && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4 z-50" onClick={() => setActiveReview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">AI-generated reply</h3>
              <button onClick={() => setActiveReview(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="mt-3 text-xs text-slate-500">Replying to {activeReview.author} ({activeReview.rating}★)</div>
            <div className="mt-2 text-sm text-slate-600 italic bg-slate-50 rounded-lg p-3">“{activeReview.text}”</div>
            <div className="mt-4 min-h-[120px] bg-orange-50 border border-orange-100 rounded-xl p-4 text-sm text-slate-800 whitespace-pre-line">
              {generating ? (
                <span className="text-slate-500">Crafting reply…</span>
              ) : reply}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => generateReply(activeReview)}>Regenerate</Button>
              <Button onClick={postReply} disabled={!reply} className="bg-slate-900 hover:bg-slate-800 text-white">Post reply</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GoogleBadge = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 4-3.8 5.3l6.5 5.5C42.6 35 44 29.9 44 24c0-1.3-.1-2.7-.4-3.5z"/>
  </svg>
);

export default GBP;
